import React, { useState } from "react";
import Modal from "./Modal";
import {
  clusterApi,
  TopicSuggestionsPayload,
  TopicSuggestionsResponse,
  ApiError,
} from "../services/api";
import "./TopicSuggestionsModal.css";

interface TopicSuggestionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  keyword: string;
}

const TopicSuggestionsModal: React.FC<TopicSuggestionsModalProps> = ({
  isOpen,
  onClose,
  keyword,
}) => {
  const [maxSuggestions, setMaxSuggestions] = useState<string>("5");
  const [suggestions, setSuggestions] =
    useState<TopicSuggestionsResponse | null>(null);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!maxSuggestions.trim()) {
      setError("Please enter the number of suggestions");
      return;
    }

    // Validate max suggestions
    if (
      isNaN(Number(maxSuggestions)) ||
      Number(maxSuggestions) < 1 ||
      Number(maxSuggestions) > 10 ||
      !Number.isInteger(Number(maxSuggestions))
    ) {
      setError("Number of suggestions must be an integer between 1 and 10");
      return;
    }

    setIsLoading(true);
    setError("");
    setSuggestions(null);

    try {
      const payload: TopicSuggestionsPayload = {
        keyword: keyword,
        num_suggestions: Number(maxSuggestions),
      };

      const response = await clusterApi.getTopicSuggestions(payload);
      setSuggestions(response);
      setHasGenerated(true);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(`Failed to get suggestions: ${err.message}`);
      } else {
        setError("Failed to get topic suggestions. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setMaxSuggestions("5");
    setSuggestions(null);
    setError("");
    setIsLoading(false);
    setHasGenerated(false);
    onClose();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMaxSuggestions(e.target.value);
    if (error) setError(""); // Clear error when user starts typing
  };

  const handleGenerateAnother = () => {
    setSuggestions(null);
    setHasGenerated(false);
    setError("");
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Topic Suggestions for "${keyword}"`}
      maxWidth="700px"
    >
      <div className="topic-suggestions-modal">
        {!hasGenerated ? (
          <form onSubmit={handleSubmit} className="suggestions-form">
            <div className="form-group">
              <label htmlFor="suggestions-input" className="form-label">
                Number of Suggestions *
              </label>
              <input
                id="suggestions-input"
                type="number"
                min="1"
                max="10"
                value={maxSuggestions}
                onChange={handleInputChange}
                placeholder="Enter number (1-10)"
                className={`form-input ${error ? "form-input-error" : ""}`}
                disabled={isLoading}
                autoFocus
              />
              <span className="field-hint">
                Generate between 1 and 10 topic suggestions for this keyword
              </span>
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="form-actions">
              <button
                type="button"
                onClick={handleClose}
                className="btn btn-secondary"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isLoading || !maxSuggestions.trim()}
              >
                {isLoading ? (
                  <>
                    <span className="loading-spinner-button"></span>
                    Generating...
                  </>
                ) : (
                  "Generate Suggestions"
                )}
              </button>
            </div>
          </form>
        ) : (
          <div className="suggestions-results">
            {isLoading ? (
              <div className="loading-state">
                <span className="loading-spinner"></span>
                <p>Generating topic suggestions...</p>
              </div>
            ) : suggestions ? (
              <>
                <div className="suggestions-header">
                  <h3>Generated Topic Suggestions</h3>
                  <p className="suggestions-count">
                    {suggestions.suggestions.length} suggestion
                    {suggestions.suggestions.length !== 1 ? "s" : ""} for "
                    {suggestions.keyword}"
                  </p>
                </div>

                <div className="suggestions-list">
                  {suggestions.suggestions.map((suggestion, index) => (
                    <div key={index} className="suggestion-item">
                      <div className="suggestion-number">{index + 1}</div>
                      <div className="suggestion-content">
                        <h4 className="suggestion-title">{suggestion.title}</h4>
                        <p className="suggestion-description">
                          {suggestion.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="form-actions">
                  <button
                    onClick={handleGenerateAnother}
                    className="btn btn-secondary"
                  >
                    Generate Different Amount
                  </button>
                  <button onClick={handleClose} className="btn btn-primary">
                    Close
                  </button>
                </div>
              </>
            ) : (
              <div className="empty-state">
                <p>No suggestions were generated. Please try again.</p>
                <div className="form-actions">
                  <button
                    onClick={handleGenerateAnother}
                    className="btn btn-secondary"
                  >
                    Try Again
                  </button>
                  <button onClick={handleClose} className="btn btn-primary">
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default TopicSuggestionsModal;
